import Layers from "slotworks/graphics/layers/layers";
import Contract from "slotworks/utils/contract";
import Pot from "./pot";
import { sequence } from "slotworks/utils/race-utils";
import gameState from "slotworks/model/game-state";
import ICSpinRecord from "integration/revolver/schema/spin-record";
import ICFreespinRecord from "integration/revolver/schema/freespin-record";
import { DimSymbolsCommand } from "slotworks/commands/symbols/dim-symbols-command";
import Components from "slotworks/components/components";
import DSComponents from "components/components";
import { StaticSymbolsCommand } from "slotworks/commands/symbols/static-symbols-command";

export enum PotType {
    MULTIPLIER,
    FREESPIN,
    BONUS
}

export default class CoinPotsComponent {

    private readonly ORDER:number[] = [ 0, 4, 2, 1, 3];

    private potsLayer: Layers;
    private coinAnimationsLayer: Layers;

    private pots: Pot[];
    private waiting: boolean[];

    private initialPersistedPots: Map<number, number[]> = new Map();
    
    constructor() {
        this.pots = [];
        this.waiting = [];        
    }    

    public init() {
        this.potsLayer = Layers.Custom.get("CoinCollectPots");
        this.potsLayer.setScene("CoinCollectPots").execute();
        this.coinAnimationsLayer = Layers.Custom.get("CoinCollectPotsAnimations");
        this.coinAnimationsLayer.setScene("CoinCollectPotsAnimations").execute(); // todo rename to something meaningful or default

        for(let index = 0; index < 5; index++) {
            this.pots[index] = new Pot(index + 1);
            this.pots[index].init();
            this.waiting[index] = false;
        }
    }

    public setPersistedPotLevels(persistedData: any) {
        if(persistedData && persistedData.potsValues) {
            this.initialPersistedPots = new Map();
            const potsValues = persistedData.potsValues;
            for(let bet in potsValues) {
                const key:number = parseInt(bet, 10);
                this.initialPersistedPots.set(key, [...potsValues[bet]]);
            }
        }
    }

    public setPotLevelsFromBet(bet:number) {
        if(this.initialPersistedPots.has(bet)) {
            this.setPotLevels(this.initialPersistedPots.get(bet));
        } else {
            this.setPotLevels([0, 0, 0, 0, 0]);
        }
    }

    public setPotLevels(levels: number[]) {
        for(let index = 0; index < levels.length; index++) {        
            this.pots[index].setCoins(levels[index]);
        }
    }

    public addCoin(pot: number) {
        this.pots[pot].addCoin();   
    }

    public getNextCoinPosition(pot:number, layer:Layers) {
        return this.pots[pot].getNextCoinPosition(layer);        
    }

    public bankCoinsOnFreespin() {
        for(let index = 0; index < this.pots.length; index++) {
            this.pots[index].bankCoins();
        }
    }

    public restoreBankedCoinsAfterFreespin() {
        for(let index = 0; index < this.pots.length; index++) {
            this.pots[index].restoreBankedCoins();
        }
    }

    public setActions(actions: string[]) {
        for(let index in actions) {
            if(actions[index] && actions[index].length > 0) {
                this.pots[index].setTriggered(true);
            }
        }
    }

    // refactor, constants and meaningful names
    public burstCoins(type: PotType, retrigger: boolean = false):Contract<void> {
        return new Contract<void>(resolve => {
            const icrecord = gameState.getCurrentGame().getCurrentRecord() as ICSpinRecord | ICFreespinRecord;  
            const isFreespin = retrigger || (icrecord instanceof ICFreespinRecord) && !((icrecord as ICFreespinRecord).freespinsTriggered);
            
            if(type === PotType.BONUS) {     
                sequence([
                    () => DimSymbolsCommand(Components.matrix.getAllSymbols()),           
                    () => this.pots[2].triggerEmptyAnimation(0, isFreespin),
                    () => StaticSymbolsCommand()
                ]).then(() => resolve(null));
            } else if(type === PotType.FREESPIN) {
                const freeSpinAmount = 5;
                sequence([
                    () => DimSymbolsCommand(Components.matrix.getAllSymbols()),  
                    () => this.pots[1].triggerEmptyAnimation(freeSpinAmount, isFreespin),
                    () => this.pots[3].triggerEmptyAnimation(freeSpinAmount, isFreespin)
                ]).then(() => { 
                    DSComponents.coinPotsTexts.hideTexts(); 
                    resolve(null); 
                });                    
            } else { // multiplier                
                // its better for this value to arrive from server but for now its not in the response, calculating
                const bothPots = this.pots[0].getTriggered() && this.pots[4].getTriggered(); // this.pots[0].getTriggered() && this.pots[4].getTriggered();
                if(this.pots[0].getTriggered() || this.pots[4].getTriggered()) {
                    let pots5xAmount = gameState.getCurrentGame().totalBet * 5; // todo consts
                    if(!pots5xAmount && isFreespin && (icrecord.addedCoins[0] > 0 || icrecord.addedCoins[4] > 0))
                        pots5xAmount = Math.floor(icrecord.instantWin / (icrecord.addedCoins[0] + icrecord.addedCoins[4]));
                    sequence([
                        () => DimSymbolsCommand(Components.matrix.getAllSymbols()),  
                        () => this.pots[0].triggerEmptyAnimation(pots5xAmount, isFreespin, bothPots),
                        () => this.pots[4].triggerEmptyAnimation(pots5xAmount, isFreespin, bothPots),
                        () => DSComponents.coinPotsTexts.finish()
                    ]).then(() => { 
                        if(!isFreespin && !this.isFreespinToBeTriggered()) {
                            this.pots[0].restoreBankedCoins();
                            this.pots[4].restoreBankedCoins();
                        }
                        DSComponents.coinPotsTexts.hideTexts();                        
                        StaticSymbolsCommand().then(() => resolve(null)); 
                    });
                } else resolve(null);
            }            
        });
    }

    public isFreespinToBeTriggered():boolean {
        return this.pots[1].getTriggered() && this.pots[3].getTriggered();
    }

    public burstCoinsFromPots(actions: string[]): Contract<void> {
        const icrecord = gameState.getCurrentGame().getCurrentRecord() as ICSpinRecord | ICFreespinRecord;  
        const isFreespin = icrecord instanceof ICFreespinRecord;
        return new Contract(resolve=>{
            const animateFuncs: Array<() => Contract<void>> = [];
            let pots5xAmount = gameState.getCurrentGame().totalBet * 5; // todo consts
            if(isFreespin && (icrecord.addedCoins[0] > 0 || icrecord.addedCoins[4] > 0))
                pots5xAmount = Math.floor(icrecord.instantWin / (icrecord.addedCoins[0] + icrecord.addedCoins[4]));
            const freeSpinAmount = 5; // todo consts            
            // todo isnt it better won amount per coin from server (its combined now)
            const amounts = [ pots5xAmount, freeSpinAmount, 0, freeSpinAmount, pots5xAmount ];
            // todo remove wheel and add it at the end
            for(let i = 0; i < this.ORDER.length; i++) {
                const index = this.ORDER[i];
                if(actions[index] && actions[index].length > 0 && this.waiting[index]) {                    
                    animateFuncs.push(()=>this.pots[index].triggerEmptyAnimation(amounts[index], isFreespin));
                    this.waiting[index] = false;
                }
            }
            sequence(animateFuncs).then(() => {
                if(!isFreespin && !icrecord.freespinsTriggered) {
                    this.restoreBankedCoinsAfterFreespin();
                }
            //    setTimeout(()=> resolve(null), 2000);
                resolve(null);
            });                        
        });
    }

}